import { useEffect } from 'react'
import { message, theme } from 'antd'
import { Outlet, useNavigate } from 'react-router-dom'
import { HorizontalContainer, LogoLoading, PageError, StaticPageError, VerticalContainer } from 'old/components/styled'
import { getShopId, hardLogout, queryClient, useAuthentication, useWindowDimensions } from 'old/utils'
import { useConfig, useCurrentShop } from 'old/api'
import { SideMenu } from 'old/components/layout/side-menu/side-menu'
import { BottomMenu } from 'old/components/layout/bottom-menu/bottom-menu'
import { LayoutHeader } from 'old/components/layout/layout-header/layout-header'
import { getLocale, updateAdminVersion, updateLocale, useTranslate } from 'utils'
import { useMe, useProfileUpdate } from 'api'
import { Me } from 'types'

export const Layout = () => {

    const navigate = useNavigate()
    const { isLoggedIn } = useAuthentication()

    useEffect(() => { !isLoggedIn && navigate('/login') }, [isLoggedIn])

    return isLoggedIn ? <LayoutLocaleWrapper/> : <></>

}

const LayoutLocaleWrapper = () => {
    
    const { data: me, isLoading: isMeLoading } = useMe()
    const locale = getLocale()

    return (
        isMeLoading ? <LogoLoading/> :
        !me ? <StaticPageError/> :
        (locale && locale !== me.locale) ? <LayoutLocaleUpdater me={me} locale={locale}/> :
        <LayoutContent/>
    )

}

const LayoutLocaleUpdater = ({ me, locale }: { me: Me, locale: string }) => {
    
    const { __ } = useTranslate()
    const { mutate: updateUser, isLoading, data: mutationResponse } = useProfileUpdate({...me, locale})

    useEffect(() => {
        updateUser()
    }, [])

    useEffect(() => {
        if(mutationResponse === 'locale_is_not_supported'){
            message.error(__('locale_is_not_supported', { locale }))
            updateLocale(me.locale)
        }else if(mutationResponse){
            const shopId = getShopId()
            queryClient.resetQueries(['config', shopId])
            queryClient.resetQueries(['me'])
        }
    }, [mutationResponse])

    return (
        isLoading ? <LogoLoading/> :
        !mutationResponse ? <LayoutContent/> :
        <LayoutContent/>
    )
    
}

const LayoutContent = () => {

    //hooks:
    const { token: { colorBgLayout, colorBgContainer, paddingXXS, margin }} = theme.useToken()
    const { SM, LG } = useWindowDimensions()
    const { data: config, isLoading: isConfigLoading } = useConfig()
    const { data: currentShop, isLoading: isShopLoading } = useCurrentShop()

    useEffect(() => {
        if(currentShop === null) hardLogout()
    }, [currentShop])

    useEffect(() => { // Admin version
        if(config && config.isMigratedToMRML){
            updateAdminVersion('v2')
        }
    }, [config])

    //render:
    return (
        (isConfigLoading || isShopLoading) ? <LogoLoading/> :
        !config ? <StaticPageError/> :
        config.isMigratedToMRML ? <LogoLoading/> :
        !currentShop ? <PageError/> : (

            <VerticalContainer style={{
                width: '100%',
                height: '100%',
                background: SM ? colorBgLayout : colorBgContainer,
                gap: 0
            }}>

                <VerticalContainer id='layoutScrollContainer' style={{ flex: 1, overflowY: 'auto', gap: 0 }}>

                    {/* header */}
                    {LG && <LayoutHeader/>}

                    <VerticalContainer style={{
                        flex: 1,
                        justifyContent: 'space-between'
                    }}>
                    
                        <HorizontalContainer style={{
                            flex: 1,
                            padding: LG ? `0 ${paddingXXS}px` : 0,
                            alignItems: 'stretch',
                            gap: margin
                        }}>

                            {/* side menu */}
                            {LG && (<div><SideMenu/></div>)}

                            {/* outlet */}
                            <div style={{ maxWidth: LG ? 'calc(100% - 243px - 8px)' : '100%', flex: 1, display: 'flex' }}>
                                <Outlet/>
                            </div>

                        </HorizontalContainer>

                    </VerticalContainer>

                </VerticalContainer>

                {/* bottom menu */}
                {!LG && <BottomMenu/>}

            </VerticalContainer>
        )
    )

}
